import { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import { UserContext } from "../../../pages/UserContext";
import useCatalogApi from "../../../hooks/useCatalogApi.hook";
import useProduct from "../../../hooks/useProduct.hook";
import useEntity from "../../../hooks/useEntity.hook";
import useCurrentOrg from "../../../hooks/useCurrentOrg.hook";
import transformCompleteCatalogToEditor from "../../../utils/transformCompleteCatalogToEditor";
import { buildEvaluationFromExistingReport } from "../utils/applyExistingReportToEvaluation";
import { ACR_EDITOR_ABOUT_URL, ENTITY_TYPE_VENDOR } from "../../../utlities/core.constants";

/**
 * Reusable "open an existing report in the editor" flow (GSAACR-356). Shared by the About-page
 * product typeahead and the "My Draft ACRs" list: given an existing report, it loads the report's
 * catalog, product, and (when applicable) vendor, builds the editor evaluation, commits it to
 * EvaluationContext, and optionally navigates to the editor About page.
 *
 * It returns structured results and never throws or renders UI, so callers own their own loading
 * indicators, confirmations, and error messaging.
 */
export default function useOpenReportInEditor() {
  const { setEvaluation, maybeOfferLocalRecovery } = useContext(EvaluationContext);
  const { getCatalog, setCatalogData } = useContext(CatalogContext);
  const { getCompleteCatalogById, getConformanceLevels } = useCatalogApi();
  const { getProduct } = useProduct();
  const { getVendor } = useEntity();
  const userContext = useContext(UserContext);
  const userEntity = userContext?.userEntity;
  const currentOrg = useCurrentOrg();
  const vendorId = currentOrg?.id ?? null;
  const navigate = useNavigate();

  /**
   * Resolve the editor catalog for catalogId: reuse CatalogContext when the complete catalog is
   * already loaded, otherwise fetch it, transform it for the editor, and cache it.
   * @returns {Promise<{ok: boolean, catalog?: object, reason?: string}>}
   */
  const ensureEditorCatalog = useCallback(
    async (catalogId) => {
      if (!catalogId) return { ok: false, reason: "missing-catalog" };
      const cached = getCatalog(catalogId);
      if (cached?.chapters?.length) {
        return { ok: true, catalog: cached };
      }
      try {
        const completeCatalog = await getCompleteCatalogById(catalogId);
        if (!completeCatalog?.standards?.length) {
          return { ok: false, reason: "empty-catalog" };
        }
        const editorCatalog = transformCompleteCatalogToEditor(completeCatalog);
        const conformanceLevels = await getConformanceLevels();
        if (conformanceLevels.length > 0) editorCatalog.conformanceLevels = conformanceLevels;
        setCatalogData(catalogId, editorCatalog);
        return { ok: true, catalog: editorCatalog };
      } catch {
        return { ok: false, reason: "catalog-load-failed" };
      }
    },
    [getCatalog, getCompleteCatalogById, getConformanceLevels, setCatalogData]
  );

  /**
   * Resolve vendor details for a report: skip entirely for agency-owned reports (no vendor), reuse
   * the current user's own entity when it matches, otherwise fetch by id.
   * @returns {Promise<{ok: boolean, vendor?: object, reason?: string}>}
   */
  const resolveReportVendor = useCallback(
    async (report) => {
      // Agency-owned reports have no vendor to resolve; only vendor-owned reports fetch vendor details.
      const reportVendorId = report?.vendorId ?? "";
      const reportAgencyId = report?.agencyId ?? "";
      const currentUserIsOwningAgency =
        Boolean(currentOrg?.isAgency) &&
        reportAgencyId &&
        String(reportAgencyId) === String(vendorId || "");
      if (!reportVendorId || currentUserIsOwningAgency) {
        return { ok: true, vendor: {} };
      }
      const requestedVendorId = String(reportVendorId);
      const currentUserVendorId = String(currentOrg?.isVendor ? vendorId || "" : "");
      if (
        userEntity?.type === ENTITY_TYPE_VENDOR &&
        currentUserVendorId &&
        requestedVendorId === currentUserVendorId &&
        userEntity?.entity
      ) {
        return { ok: true, vendor: userEntity.entity };
      }
      try {
        const vendorApi = await getVendor(reportVendorId);
        return { ok: true, vendor: vendorApi };
      } catch {
        return { ok: false, reason: "vendor-load-failed" };
      }
    },
    [currentOrg, vendorId, userEntity, getVendor]
  );

  /**
   * Load + build + commit the editor evaluation for an existing report.
   * @param {{report: object, previousEvaluation?: object, navigateToEditor?: boolean}} args
   * @returns {Promise<{ok: boolean, reason?: string, catalogId?: string}>}
   */
  const openReportInEditor = useCallback(
    async ({ report, previousEvaluation, navigateToEditor = false }) => {
      if (report == null || typeof report !== "object") {
        return { ok: false, reason: "missing-report" };
      }
      const { catalogId } = report;
      const catalogResult = await ensureEditorCatalog(catalogId);
      if (!catalogResult.ok) {
        return { ok: false, reason: catalogResult.reason ?? "catalog-load-failed" };
      }

      let productApi;
      try {
        productApi = await getProduct(report.productId);
      } catch {
        return { ok: false, reason: "product-load-failed" };
      }

      const vendorResult = await resolveReportVendor(report);
      if (!vendorResult.ok) {
        return { ok: false, reason: vendorResult.reason ?? "vendor-load-failed" };
      }

      const serverEvaluation = buildEvaluationFromExistingReport({
        previousEvaluation,
        report,
        productApi,
        vendorApi: vendorResult.vendor,
        editorCatalog: catalogResult.catalog,
      });
      setEvaluation(serverEvaluation);
      // Offer to recover a diverging on-device copy (edits the server never received) before the
      // debounced writer overwrites this report's scope with the just-loaded server copy.
      maybeOfferLocalRecovery(serverEvaluation);

      if (navigateToEditor) {
        navigate(ACR_EDITOR_ABOUT_URL);
      }
      return { ok: true, catalogId };
    },
    [ensureEditorCatalog, getProduct, resolveReportVendor, setEvaluation, maybeOfferLocalRecovery, navigate]
  );

  return { openReportInEditor, ensureEditorCatalog };
}
